import { format as fnsFormat } from 'date-fns';

import type { Chip, Suggestion } from '@/features/search-bar/types';

/**
 * Serialise a Date to YYYY-MM-DD using LOCAL calendar parts — never .toISOString()
 * which converts to UTC and can shift the date by up to ±14 h.
 */
export const toDateParam = (d: Date): string =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export const formatDate = (date: Date | null): string => {
    if (!date || isNaN(date.getTime())) return '';
    // Format directly from the Date object (already browser-local time).
    // Never go through window.Time.format for calendar dates — it converts
    // via parseISO (midnight UTC) then shifts to the motel timezone, which
    // can move the displayed date ±1 day.
    return fnsFormat(date, 'EEE, MMM d');
};

export const formatHistoryDate = (dateStr: string): string => {
    // Parse as local date (not UTC) then format directly.
    const [y, m, d] = dateStr.split('-').map(Number);
    return fnsFormat(new Date(y, m - 1, d), 'MMM d');
};

export const getSuggestionSubtitle = (s: Suggestion): string => {
    switch (s.type) {
        case 'motel':
            return [s.location, s.country].filter(Boolean).join(', ');
        case 'city':
            return [s.state, s.country].filter(Boolean).join(', ');
        case 'state':
            return s.country ?? '';
        default:
            return '';
    }
};

export const getSuggestionLabel = (s: Suggestion): string => {
    const map: Record<Suggestion['type'], string> = {
        motel: 'Motel',
        state: 'State',
        country: 'Country',
        city: 'City',
    };
    return map[s.type];
};

export const buildChips = (): Chip[] => {
    const today = new Date();
    today.setHours(12, 0, 0, 0);

    const midday = (d: Date) => {
        d.setHours(12, 0, 0, 0);
        return d;
    };
    const addDays = (d: Date, n: number) =>
        midday(new Date(new Date(d).setDate(d.getDate() + n)));

    const tomorrow = addDays(today, 1);
    const dayOfWeek = today.getDay();
    const daysToSat = (6 - dayOfWeek + 7) % 7 || 7;
    const thisSat = addDays(today, daysToSat);
    const nextSat = addDays(thisSat, 7);

    return [
        { label: 'Tonight', action: (sd) => sd(today, addDays(today, 1)) },
        {
            label: 'Tomorrow',
            action: (sd) => sd(tomorrow, addDays(tomorrow, 1)),
        },
        { label: 'Weekend', action: (sd) => sd(thisSat, addDays(thisSat, 1)) },
        {
            label: 'Next weekend',
            action: (sd) => sd(nextSat, addDays(nextSat, 1)),
        },
        { label: 'Clear', action: (sd) => sd(null, null) },
    ];
};
